/** Copyright 2026 Midas Healthcare Solutions - All Rights Reserved **/
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
  NgZone,
  ChangeDetectorRef,
  SimpleChanges,
  OnChanges,
  OnInit,
} from '@angular/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import {
  FormularyWitnessRequirement,
  isTransport,
  isWasting,
  GeneralSettings,
  IWorkflow,
  LANDSCAPE_ASPECT_RATIO,
  LANDSCAPE_ASPECT_RATIO_VALUE,
  PORTRAIT_ASPECT_RATIO,
  PORTRAIT_ASPECT_RATIO_VALUE,
  ViewVideoMetadata,
  isBlueCheckBag,
  FeatureFlagNames,
} from '@midas/shared/common';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { VideoEvent, VideoEventType } from '@midas/shared/common';
import { VideoThumbnailComponent } from '../video-thumbnail.component';
import { UserSelectors, FeatureFlagsActions, FeatureFlagsSelectors } from '@midas/shared/data-access';
import { Store } from '@ngrx/store';
import { takeUntil, combineLatest, take } from 'rxjs';
import { OverlayContainer } from '@angular/cdk/overlay';
import { MatDialog } from '@angular/material/dialog';
import { VideoOverlayHelpDialogComponent } from './video-overlay-help-dialog.component';
import { RxJSBaseClass } from '@midas/shared/util';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const videojs = (window as any).videojs;
const DEFAULT_WITNESS_VIDEO_WIDTH = 400; // default width 25 x 16
const DEFAULT_WITNESS_VIDEO_HEIGHT = 225; // default width 25 x 9
const SCREEN_CAPTURE_WINDOW_FRACTION = 0.4;
const SCREEN_CAPTURE_EDGE_MARGIN = 50; // px

/** ~40% of the window, constrained to a 16x9 landscape box that fits within that 40% in both dimensions. */
function defaultScreenCaptureSize(): { width: number; height: number } {
  const maxWidth = window.innerWidth * SCREEN_CAPTURE_WINDOW_FRACTION;
  const maxHeight = window.innerHeight * SCREEN_CAPTURE_WINDOW_FRACTION;
  const width = Math.round(Math.min(maxWidth, (maxHeight * 16) / 9));
  return { width, height: Math.round((width * 9) / 16) };
}
type ResizeMode = 'screen' | 'live';

interface TimeSegment {
  recStart: number;
  recEnd: number;
  vidStart: number;
  vidEnd: number;
}

@Component({
  selector: 'midas-vjs-overlay-player',
  styles: [
    `
      .metrics {
        display: flex;
        justify-content: space-between;
        padding: 0 0 0 0;
        margin-top: 6px;
        border: 1px solid transparent;
        border-radius: 4px;
        background-color: var(--bg-block);
        font-size: smaller;
        height: 49px;
      }

      .metrics div {
        padding: 5px;
        width: 15%;
      }

      .metrics p {
        padding: 0px;
        margin: 0px;
        color: var(--mat-expansion-header-text-color, var(--mat-sys-on-surface));
        padding-bottom: 4px;
      }

      .red {
        color: red;
      }

      .green {
        color: green;
      }

      .orange {
        color: orange;
      }
    `,
  ],
  template: `
    <div #videoWrapper [attr.id]="instanceId" class="video-wrapper">
      <video
        #backgroundVideo
        id="backgroundPlayer"
        class="video-js"
        [style.display]="!workflow?.videoUploadComplete || videoCheckSumFail ? 'none' : 'block'"
        crossorigin="anonymous"
        controls
        muted
        playsinline
        [preload]="preload"
        (error)="handleVideoError($event)"
      ></video>
      <midas-video-thumbnail
        [workflow]="workflow"
        [videoCheckSumFail]="videoCheckSumFail"
        [width]="width"
        [height]="height"
        [videoNotAvailable]="videoNotAvailable"
      />
      @if (showVideoError) {
        <div>{{ videoLoadErrorMessage }}</div>
        <div>{{ videoLoadErrorDetail }}</div>
      }

      <div
        [hidden]="!showLiveStream || !showOverlays"
        class="live-overlay-video-div"
        cdkDrag
        cdkDragBoundary=".video-wrapper"
        [ngStyle]="{
          width: liveOverlayWidth + 'px',
          height: liveOverlayHeight + 'px',
          top: liveOverlayTop + 'px',
          left: liveOverlayLeft + 'px',
        }"
      >
        <video
          #liveOverlayVideo
          id="liveOverlayPlayer"
          class="video-js"
          crossorigin="anonymous"
          autoplay
          muted
          disablepictureinpicture
        ></video>
        @if (enable360OverlayDragHandle) {
          <div class="move-handle" cdkDragHandle>
            <mat-icon color="primary">open_with</mat-icon>
          </div>
        }
        <div class="resize-handle" (mousedown)="startResize($event, 'live')">
          <mat-icon color="primary">zoom_out_map</mat-icon>
        </div>
      </div>

      <div
        [hidden]="!showScreenCapture || !showOverlays"
        class="overlay-video-div"
        cdkDrag
        cdkDragBoundary=".video-wrapper"
        [ngStyle]="{
          width: overlayWidth + 'px',
          height: overlayHeight + 'px',
          top: overlayTop + 'px',
          left: overlayLeft + 'px',
        }"
      >
        <video
          #overlayVideo
          id="overlayPlayer"
          class="video-js"
          crossorigin="anonymous"
          controls
          muted
          playsinline
          [preload]="preload"
          (error)="handleScreenVideoError()"
        ></video>
        <div class="resize-handle" (mousedown)="startResize($event, 'screen')">
          <mat-icon color="primary">zoom_out_map</mat-icon>
        </div>
      </div>

      <div class="metrics" *ngIf="isMidasAdmin && !videoCheckSumFail && workflow?.videoUploadComplete && showVideoMetrics">
        <div>
          <p>Buffering count:</p>
          <span>{{ bufferingCount }}</span>
        </div>
        <div>
          <p>Total buffering:</p>
          <span>{{ totalBufferingTime | number: '1.0-0' }} ms</span>
        </div>
        <div>
          <p>Startup time:</p>
          <span>{{ startUpTime | number: '1.0-0' }} ms</span>
        </div>
        <div *ngIf="bufferRate > 0">
          <p>Buffer rate:</p>
          <span [ngClass]="bufferRate >= 1.5 ? 'green' : bufferRate >= 1.2 ? 'orange' : 'red'">
            {{ bufferRate | number: '1.2-2' }}
          </span>
        </div>
        <div *ngIf="averageBufferRate >= 1.5" class="green">Excellent playback</div>
        <div *ngIf="averageBufferRate >= 1.2 && averageBufferRate < 1.5" class="orange">Stable</div>
        <div *ngIf="averageBufferRate > 1 && averageBufferRate < 1.2" class="orange">Borderline</div>
        <div *ngIf="averageBufferRate <= 1 && averageBufferRate > 0" class="red">Buffering likely</div>
        <div class="red" *ngIf="averageBufferRate <= 0">No buffer growth</div>
        <div *ngIf="averageBufferRate > 0">
          <p>Avg buffer rate:</p>
          <span [ngClass]="averageBufferRate >= 1.5 ? 'green' : averageBufferRate >= 1.0 ? 'orange' : 'red'">
            {{ averageBufferRate | number: '1.2-2' }}
          </span>
        </div>
      </div>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  styleUrls: ['vjs-overlay-player.component.scss'],
  imports: [CommonModule, TranslocoModule, MatIconModule, DragDropModule, VideoThumbnailComponent],
})
export class VjsOverlayPlayerComponent
  extends RxJSBaseClass
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  @ViewChild('backgroundVideo', { static: true }) backgroundVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('overlayVideo', { static: true }) overlayVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('liveOverlayVideo', { static: true }) liveOverlayVideo!: ElementRef<HTMLVideoElement>;

  @Input() preload: 'auto' | 'metadata' = 'metadata';
  @Input() @HostBinding('style.width.px') width = 220;
  @Input() @HostBinding('style.height.px') height = 140;

  @Output() videoFailedEvent = new EventEmitter();
  @Output() metadataLoaded = new EventEmitter<{ duration: number }>();
  @Output() videoEventOccurred = new EventEmitter<{ event: VideoEvent }>();

  // See options: https://videojs.com/guides/options

  @Input() fluid = false;
  @Input() autoplay = false;
  @Input() disabled = false;
  @Input() viewVideoMetadata?: ViewVideoMetadata | null;
  @Input() showScreenCapture = false;
  @Input() showVideoMetrics = false;
  @Input() showLiveStream = false;
  @Input() witnessDriven = false;
  @Input() type = 'video/mp4';
  @Input() workflow?: IWorkflow;
  @Input() inactivityTimeout = 0; // No inactivity timeout treats the user as always active. Controls will not hide.
  @Input() videoNotAvailable = false;
  @Input() videoRecordingTime?: number = 0;

  private static instanceCounter = 0;
  readonly instanceId = `vjs-overlay-${VjsOverlayPlayerComponent.instanceCounter++}`;

  public bufferingCount = 0;
  public startUpTime = 0;
  private metricsStartTime = 0;
  private bufferingStart: number | null = null;
  public totalBufferingTime = 0;
  public bandwidthMbps = 0;

  private lastBufferCheckTime = 0;
  private lastBufferedEnd = 0;
  public bufferRate = 0;
  private bufferSamples: number[] = [];
  public averageBufferRate = 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  backgroundPlayer: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overlayPlayer: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  liveOverlayPlayer: any;

  src = ''; // Wasting video OR witness video if witnessDriven = true
  overlaySrc = ''; // Screen Capture
  liveOverlaySrc = ''; // Live witness feed for Anytime Witness OR wasting video if witnessDriven = true
  videoCheckSumFail = false;
  witnessDrivenSegments: TimeSegment[] = [];

  showVideoError = false;
  videoLoadErrorMessage = '';
  videoLoadErrorDetail = '';
  showOverlays = false;
  overlayWidth = defaultScreenCaptureSize().width;
  overlayHeight = defaultScreenCaptureSize().height;
  overlayLeft = 0;
  overlayTop = 0;
  liveOverlayWidth = DEFAULT_WITNESS_VIDEO_WIDTH;
  liveOverlayHeight = DEFAULT_WITNESS_VIDEO_HEIGHT;
  liveOverlayLeft = 0;
  liveOverlayTop = 0;
  portraitMode = false;
  viewVideoTimeOffset = 0;
  lastSeekEvent = 0;
  lastSeekedEvent = 0;
  witnessDuration = 0; // Witness recordings aren't real MP4s so duration must be calculated and assigned manually to allow scrubbing
  enable360OverlayDragHandle = false; // Add a specific handle to drag video so 360 overlay video doesn't simultaneously move and pan
  showHelpDialog = true;
  isMidasAdmin = false;

  fullScreenHandler!: () => void;

  constructor(
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private overlay: OverlayContainer,
    private store: Store,
    private transloco: TranslocoService,
  ) {
    super();
  }

  ngOnInit(): void {
    // Override witness driven video if not completed anytime witness waste
    if (
      !(
        (isWasting(this.workflow) &&
          this.workflow.witnessType === FormularyWitnessRequirement.Anytime &&
          this.workflow.witnessUserId !== null) ||
        (isTransport(this.workflow) && this.workflow.transportWitnessUserId !== null) ||
        isBlueCheckBag(this.workflow)
      )
    ) {
      this.witnessDriven = false;
    }

    // Show live stream means anytime witness
    if (this.showLiveStream) {
      this.src = this.workflow?.videoUrls?.videoReadUrl ?? '';
      this.overlaySrc = this.workflow?.videoUrls?.screenVideoReadUrl ?? '';
      if (!this.overlaySrc) this.showScreenCapture = false;
      this.videoCheckSumFail = this.workflow?.videoUrls?.videoCheckSumFail ?? true;
    } else {
      if (this.witnessDriven) {
        this.src = this.workflow?.videoUrls?.witnessVideoReadUrl ?? '';
        this.liveOverlaySrc = this.workflow?.videoUrls?.videoReadUrl ?? '';
        this.showLiveStream = !!this.liveOverlaySrc;

        let events: VideoEvent[] = [];
        try {
          if (isWasting(this.workflow)) {
            events = JSON.parse(this.workflow.witnessVideoMetadata || '[]');
          } else if (isTransport(this.workflow)) {
            events = JSON.parse(this.workflow.witnessVideoMetadata || '[]');
          }
        } catch {
          // Handle invalid witness metadata JSON
        }

        // Check if witness driven events exist, otherwise show only witness video on its own
        if (events.length > 0) {
          this.witnessDuration = events.reduce((max, obj) => {
            const t = obj.startVideoRecordingTime ?? 0;
            return t > max ? t : max;
          }, 0);
          this.buildSegments(events);
        } else {
          this.showLiveStream = false;
          this.showScreenCapture = false;
        }
      } else {
        this.src = this.workflow?.videoUrls?.videoReadUrl ?? '';
      }

      this.overlaySrc = this.workflow?.videoUrls?.screenVideoReadUrl ?? '';
      if (!this.overlaySrc) this.showScreenCapture = false;
    }

    combineLatest([
      this.store.select(UserSelectors.getUserAlibiGeneralSetting(GeneralSettings.HideVideoOverlayHelp)),
      this.store.select(UserSelectors.getUserState),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([hideVideoOverlayHelp, user]) => {
        this.showHelpDialog = !hideVideoOverlayHelp;
        this.isMidasAdmin = user.midasAdmin;
      });

      this.store
        .select(FeatureFlagsSelectors.isFeatureEnabled(FeatureFlagNames.VideoMetrics))
        .pipe(take(1))
        .subscribe((videoMetricsEnabled) => (this.showVideoMetrics = videoMetricsEnabled));
  }

  @ViewChild('videoWrapper', { static: true }) videoWrapper!: ElementRef<HTMLDivElement>;
  ngOnChanges(changes: SimpleChanges) {
    if (changes['videoRecordingTime']) {
      this.videoRecordingTime = changes['videoRecordingTime'].currentValue;
    }
  }

  ngAfterViewInit() {
    if (this.viewVideoMetadata) {
      const cameraStartTime = new Date(this.viewVideoMetadata.CameraVideo.StartTime).getTime();
      const screenCapStartTime = new Date(this.viewVideoMetadata.ScreenCaptureVideo.StartTime).getTime();

      this.viewVideoTimeOffset = (screenCapStartTime - cameraStartTime) / 1000;
    }

    this.backgroundPlayer = videojs(this.backgroundVideo.nativeElement, {
      fluid: this.fluid,
      aspectRatio: LANDSCAPE_ASPECT_RATIO,
      autoplay: this.autoplay,
      controls: !this.disabled,
      sources: [{ src: this.src, type: this.type }],
      inactivityTimeout: this.inactivityTimeout,
      disablePictureInPicture: true,
      controlBar: { pictureInPictureToggle: false, volumePanel: false },
      userActions: { doubleClick: false }, // double click full screens the video instead of the wrapper, just disable it
    });

    this.metricsStartTime = performance.now();

    const videoEl = this.backgroundVideo.nativeElement;

    this.backgroundVideo.nativeElement.addEventListener('loadedmetadata', (e: Event) => {
      const video = e.target as HTMLVideoElement;
      this.metadataLoaded.emit({ duration: video.duration });

      if (this.witnessDriven) {
        this.backgroundPlayer.duration(this.witnessDuration);
      }

      if (!this.witnessDriven && this.workflow?.is360Video) {
        this.backgroundPlayer.vr({ projection: 'Sphere' });

        // Zooming functionality
        setTimeout(() => {
          const vr = this.backgroundPlayer.vr();
          const canvas = vr?.renderedCanvas as HTMLCanvasElement | undefined;
          const camera = vr?.camera as any;

          if (!canvas || !camera) return;

          canvas.addEventListener(
            'wheel',
            (e: WheelEvent) => {
              e.preventDefault();

              const minFov = 30;
              const maxFov = 100;
              const step = 4;

              camera.fov = Math.max(minFov, Math.min(maxFov, camera.fov + (e.deltaY > 0 ? step : -step)));
              camera.updateProjectionMatrix();
            },
            { passive: false },
          );
        }, 0);
      }
    });

    this.backgroundPlayer.on('loadstart', () => {
      this.metricsStartTime = performance.now();
      this.startUpTime = 0;
    });

    this.backgroundPlayer.on('loadeddata', () => {
      const startupTime = performance.now() - this.metricsStartTime;
      this.startUpTime = startupTime;
    });

    this.backgroundPlayer.on('progress', () => {
      const videoEl = this.backgroundVideo.nativeElement;

      if (!videoEl || !videoEl.buffered || videoEl.buffered.length === 0) {
        return;
      }

      const now = performance.now();
      // latest buffered point (end of last buffer range)
      const bufferedEnd = videoEl.buffered.end(videoEl.buffered.length - 1);
      const duration = videoEl.duration;

      // Detect fully buffered
      const isFullyBuffered = duration !== undefined && duration > 0 && bufferedEnd >= duration - 0.5;

      if (isFullyBuffered) {
        // Freeze buffer rate at "good"
        this.bufferRate = Math.max(this.bufferRate, 1.5);
        return;
      }

      // Normal calculation
      if (!isFullyBuffered) {
        if (this.lastBufferCheckTime > 0) {
          const timeDiff = (now - this.lastBufferCheckTime) / 1000; // seconds
          const bufferDiff = bufferedEnd - this.lastBufferedEnd; // seconds of video

          if (timeDiff > 0 && bufferDiff >= 0) {
            this.bufferRate = bufferDiff / timeDiff;
            this.bufferSamples.push(this.bufferRate);
            // calculate average
            const sum = this.bufferSamples.reduce((a, b) => a + b, 0);
            this.averageBufferRate = sum / this.bufferSamples.length;
          }

          if (bufferDiff === 0) {
            this.bufferRate = 0;
          }
        }

        this.lastBufferCheckTime = now;
        this.lastBufferedEnd = bufferedEnd;
      }
    });

    this.overlayPlayer = videojs(this.overlayVideo.nativeElement, {
      fluid: this.fluid,
      aspectRatio: LANDSCAPE_ASPECT_RATIO,
      autoplay: this.autoplay,
      controls: false,
      sources: [{ src: this.overlaySrc, type: this.type }],
      inactivityTimeout: this.inactivityTimeout,
    });
    this.enforceMuted(this.backgroundVideo.nativeElement);
    this.enforceMuted(this.overlayVideo.nativeElement);

    this.overlayVideo.nativeElement.addEventListener('loadedmetadata', () => {
      if (this.overlayVideo.nativeElement.videoHeight > this.overlayVideo.nativeElement.videoWidth) {
        this.overlayPlayer.aspectRatio(PORTRAIT_ASPECT_RATIO);
        this.portraitMode = true;
        const size = defaultScreenCaptureSize();
        this.overlayWidth = size.height;
        this.overlayHeight = size.width;
        this.positionOverlayBottomRight();
      }
    });
    this.positionOverlayBottomRight();
    this.positionLiveOverlayTopRight();

    this.backgroundPlayer.ready(() => {
      const fullscreenToggle = this.backgroundPlayer.controlBar.getChild('FullscreenToggle');
      if (fullscreenToggle) {
        fullscreenToggle.off('click');
        fullscreenToggle.on('click', () => {
          const wrapper = this.videoWrapper.nativeElement;
          if (document.fullscreenElement?.id === wrapper.id) {
            document.exitFullscreen();
          } else {
            wrapper.requestFullscreen();
          }
        });
      }
    });

    if (this.liveOverlaySrc) {
      this.liveOverlayPlayer = videojs(this.liveOverlayVideo.nativeElement, {
        fluid: this.fluid,
        aspectRatio: LANDSCAPE_ASPECT_RATIO,
        autoplay: this.autoplay,
        controls: false,
        sources: [{ src: this.liveOverlaySrc, type: this.type }],
        inactivityTimeout: this.inactivityTimeout,
      });
      this.enforceMuted(this.liveOverlayVideo.nativeElement);

      this.liveOverlayVideo.nativeElement.addEventListener('loadedmetadata', () => {
        if (this.witnessDriven && this.workflow?.is360Video) {
          this.enable360OverlayDragHandle = true;
          this.liveOverlayPlayer.vr({ projection: 'Sphere' });
        }
      });
    }

    this.fullScreenHandler = () => {
      this.ngZone.run(() => {
        if (document.fullscreenElement?.id === this.videoWrapper.nativeElement.id) {
          const container = this.overlay.getContainerElement();

          this.showOverlays = true;
          this.cdr.detectChanges();

          if (this.witnessDriven && this.workflow?.is360Video) {
            requestAnimationFrame(() => this.liveOverlayPlayer?.vr().handleResize_());
          }

          if (this.showHelpDialog) {
            this.videoWrapper.nativeElement.appendChild(container);
            this.backgroundPlayer.pause();
            this.showHelpDialog = false; // Show only once per wasting

            this.dialog.open(VideoOverlayHelpDialogComponent, {
              panelClass: 'ui-dialog-window',
              minHeight: '180px',
              maxHeight: '180px',
              minWidth: '500px',
              maxWidth: '500px',
              disableClose: true,
            });
          } else {
            document.body.appendChild(container);
          }
        } else {
          this.showOverlays = false;
        }
      });
    };

    document.addEventListener('fullscreenchange', this.fullScreenHandler);

    this.backgroundPlayer.on('play', () => {
      const currentTime = this.backgroundPlayer.currentTime() + this.viewVideoTimeOffset;
      const videoEvent = {
        startWatchVideoTime: currentTime,
        captureEventType: VideoEventType.Play,
        startVideoRecordingTime: this.videoRecordingTime,
      } as VideoEvent;
      this.videoEventOccurred.emit({ event: videoEvent });

      this.overlayPlayer.currentTime(currentTime || 0);
      this.overlayPlayer.play();

      if (this.witnessDriven) {
        this.liveOverlayPlayer.currentTime(currentTime || 0);
        this.liveOverlayPlayer.play();
      }
    });

    this.backgroundPlayer.on('pause', () => {
      const currentTime = this.backgroundPlayer.currentTime() + this.viewVideoTimeOffset;
      const videoEvent = {
        startWatchVideoTime: currentTime,
        captureEventType: VideoEventType.Pause,
        startVideoRecordingTime: this.videoRecordingTime,
      } as VideoEvent;
      this.videoEventOccurred.emit({ event: videoEvent });
      this.overlayPlayer.pause();

      if (this.witnessDriven) {
        this.liveOverlayPlayer.pause();
      }
    });

    this.backgroundPlayer.on('seeking', () => {
      const currentTime = this.backgroundPlayer.currentTime() + this.viewVideoTimeOffset;
      this.overlayPlayer.currentTime(currentTime || 0);
      if (this.witnessDriven) {
        this.liveOverlayPlayer.currentTime(currentTime || 0);
      }

      if (this.videoRecordingTime && this.videoRecordingTime !== this.lastSeekEvent) {
        const videoEvent = {
          startWatchVideoTime: currentTime,
          captureEventType: VideoEventType.StartScrub,
          startVideoRecordingTime: this.videoRecordingTime,
        } as VideoEvent;
        this.videoEventOccurred.emit({ event: videoEvent });
        this.lastSeekEvent = this.videoRecordingTime;
      }
    });

    this.backgroundPlayer.on('seeked', () => {
      const currentTime = this.backgroundPlayer.currentTime() + this.viewVideoTimeOffset;

      if (this.videoRecordingTime && this.videoRecordingTime !== this.lastSeekedEvent) {
        const videoEvent = {
          endWatchVideoTime: currentTime,
          captureEventType: VideoEventType.EndScrub,
          endVideoRecordingTime: this.videoRecordingTime,
        } as VideoEvent;
        this.videoEventOccurred.emit({ event: videoEvent });
        this.lastSeekedEvent = this.videoRecordingTime;
      }
    });

    this.backgroundPlayer.on('timeupdate', () => {
      if (this.witnessDriven) {
        const witnessTime = this.backgroundPlayer.currentTime();
        const wastingTime = this.mapWitnessTimeToWastingTime(witnessTime);
        if (wastingTime == null) {
          this.overlayPlayer.pause();
          this.liveOverlayPlayer.pause();
          return;
        }

        this.overlayPlayer.currentTime(wastingTime);
        this.liveOverlayPlayer.currentTime(wastingTime);
      } else {
        const drift = Math.abs(
          (this.backgroundPlayer.currentTime() + this.viewVideoTimeOffset || 0) -
            (this.overlayPlayer.currentTime() || 0),
        );
        if (drift > 0.3) {
          this.overlayPlayer.currentTime(this.backgroundPlayer.currentTime() + this.viewVideoTimeOffset || 0);
        }
      }
    });

    // === METRICS: Buffering ===
    this.backgroundPlayer.on('waiting', () => {
      this.bufferingCount++;
      this.bufferingStart = performance.now();
    });

    this.backgroundPlayer.on('playing', () => {
      if (this.bufferingStart) {
        const duration = performance.now() - this.bufferingStart;
        this.totalBufferingTime += duration;
        this.bufferingStart = null;
        const currentTime = this.backgroundPlayer.currentTime();
      }
    });
  }

  override ngOnDestroy() {
    document.removeEventListener('fullscreenchange', this.fullScreenHandler);

    if (this.overlayPlayer) this.overlayPlayer.dispose();
    if (this.liveOverlayPlayer) this.liveOverlayPlayer.dispose();
    if (this.backgroundPlayer) this.backgroundPlayer.dispose();
    super.ngOnDestroy();
  }

  private enforceMuted(video: HTMLVideoElement): void {
    video.muted = true;
    video.addEventListener('volumechange', () => {
      if (!video.muted) video.muted = true;
    });
  }

  @HostListener('window:blur')
  onBlur(): void {
    this.backgroundPlayer.pause();
    this.overlayPlayer?.pause();
    this.liveOverlayPlayer?.pause();
  }

  handleVideoError(e: ErrorEvent): void {
    this.showVideoError = true;
    this.videoFailedEvent.emit(true);
    this.videoLoadErrorMessage = this.transloco.translate('WitnessPlayVideoError');

    if (!e) {
      this.videoLoadErrorDetail =
        this.transloco.translate('ErrorDetail') + ': ' + this.transloco.translate('UnknownError');
      return;
    }

    const video = e.target as HTMLVideoElement;
    this.videoLoadErrorDetail = this.transloco.translate('ErrorDetail') + ': ';

    switch (video.error?.code) {
      case MediaError.MEDIA_ERR_ABORTED:
        this.videoLoadErrorDetail =
          this.videoLoadErrorDetail + this.transloco.translate('FetchAbortedByUser');
        break;
      case MediaError.MEDIA_ERR_NETWORK:
        this.videoLoadErrorDetail = this.videoLoadErrorDetail + this.transloco.translate('MediaNetworkIssue');
        break;
      case MediaError.MEDIA_ERR_DECODE:
        this.videoLoadErrorDetail = this.videoLoadErrorDetail + this.transloco.translate('MediaDecodeError');
        break;
      case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
        this.videoLoadErrorDetail =
          this.videoLoadErrorDetail + this.transloco.translate('MediaSourceUnavailable');
        break;
      default:
        this.videoLoadErrorDetail =
          this.videoLoadErrorDetail +
          this.transloco.translate('UnknownMediaErrorCode') +
          ': ' +
          video.error?.code;
    }
  }

  buildSegments(events: VideoEvent[]) {
    let segment: { recStart: number; vidStart: number } | null = null;
    let isPlaying = false;

    for (const event of events) {
      // Takes into account video continues to play if scrubbed while playing
      if (event.captureEventType === VideoEventType.Play) isPlaying = true;
      else if (event.captureEventType === VideoEventType.Pause) isPlaying = false;

      switch (event.captureEventType) {
        case VideoEventType.Play: {
          if (event.startVideoRecordingTime == null || event.startWatchVideoTime == null) break;
          segment = {
            recStart: event.startVideoRecordingTime,
            vidStart: event.startWatchVideoTime,
          };
          break;
        }

        case VideoEventType.Pause:
        case VideoEventType.StartScrub: {
          if (!segment) break;
          const recEnd = event.endVideoRecordingTime ?? event.startVideoRecordingTime ?? segment.recStart;
          const vidEnd = event.endWatchVideoTime ?? segment.vidStart + (recEnd - segment.recStart);

          this.witnessDrivenSegments.push({
            recStart: segment.recStart,
            recEnd,
            vidStart: segment.vidStart,
            vidEnd,
          });

          segment = null;
          break;
        }

        case VideoEventType.EndScrub: {
          if (isPlaying) {
            if (event.endVideoRecordingTime == null || event.endWatchVideoTime == null) break;
            segment = {
              recStart: event.endVideoRecordingTime,
              vidStart: event.endWatchVideoTime,
            };
          }
          break;
        }

        default:
          break;
      }
    }
  }

  mapWitnessTimeToWastingTime(tRec: number): number | null {
    const segment = this.witnessDrivenSegments.find((s) => tRec >= s.recStart && tRec <= s.recEnd);
    if (!segment) return null;

    const offset = tRec - segment.recStart;
    const tVid = segment.vidStart + offset;

    return Math.min(Math.max(tVid, segment.vidStart), segment.vidEnd);
  }

  handleScreenVideoError(): void {
    // TODO: Show actual error message or something, squash errors for now
    this.showScreenCapture = false;
  }

  /** Places the screen capture overlay in the lower right corner, fully on screen, with a margin from the edges. */
  private positionOverlayBottomRight(): void {
    this.overlayLeft = Math.max(0, window.innerWidth - this.overlayWidth - SCREEN_CAPTURE_EDGE_MARGIN);
    this.overlayTop = Math.max(0, window.innerHeight - this.overlayHeight + SCREEN_CAPTURE_EDGE_MARGIN);
  }

  /** Places the live overlay in the upper right corner with the same edge margin. */
  private positionLiveOverlayTopRight(): void {
    this.liveOverlayLeft = Math.max(0, window.innerWidth - this.liveOverlayWidth - SCREEN_CAPTURE_EDGE_MARGIN);
    this.liveOverlayTop = SCREEN_CAPTURE_EDGE_MARGIN;
  }

  private resizing: ResizeMode | null = null;
  private startX = 0;
  private startWidth = 0;

  startResize(event: MouseEvent, resizing: ResizeMode) {
    event.stopPropagation(); // prevents dragging while resizing
    event.preventDefault();

    this.resizing = resizing;
    this.startX = event.clientX;

    switch (this.resizing) {
      case 'screen':
        this.startWidth = this.overlayWidth;
        break;
      case 'live':
        this.startWidth = this.liveOverlayWidth;
        break;
      default:
        this.resizing = null;
        break;
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.resizing) return;

    const dx = event.clientX - this.startX;
    let newWidth = this.startWidth + dx;

    newWidth = Math.max(
      200,
      Math.min(newWidth, this.resizing === 'screen' && this.portraitMode ? 500 : 1200),
    );

    if (this.resizing === 'screen') {
      this.overlayWidth = newWidth;
      this.overlayHeight =
        newWidth / (this.portraitMode ? PORTRAIT_ASPECT_RATIO_VALUE : LANDSCAPE_ASPECT_RATIO_VALUE);
    } else if (this.resizing === 'live') {
      this.liveOverlayWidth = newWidth;
      this.liveOverlayHeight = newWidth / LANDSCAPE_ASPECT_RATIO_VALUE;
    }
  }

  @HostListener('document:mouseup')
  stopResize() {
    if (this.resizing === 'live' && this.witnessDriven && this.workflow?.is360Video)
      this.liveOverlayPlayer?.vr().handleResize_();

    this.resizing = null;
  }
}
